import { supabase } from '../config/supabase';
import { logError } from '../utils/errorHandling';

// Google 로그인
export const signInWithGoogle = async () => {
  try {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`
      }
    });

    if (error) {
      throw error;
    }

    return data;
  } catch (error) {
    logError('signInWithGoogle', error);
    throw error;
  }
};

// 카카오 로그인 (Supabase에서는 OAuth 제공자가 아니므로 커스텀 구현)
export const initiateKakaoLogin = async () => {
  try {
    const KAKAO_REST_API_KEY = 'feeb3a5546b4caa1e4e3364aee4b583e';
    const REDIRECT_URI = `${window.location.origin}/auth/kakao/callback`;

    // 상태 값 생성 (CSRF 방지)
    const state = Math.random().toString(36).substring(2) + Date.now().toString(36);
    localStorage.setItem('kakao_state', state);

    // 카카오 로그인 URL 생성
    const kakaoAuthUrl = new URL('https://kauth.kakao.com/oauth/authorize');
    kakaoAuthUrl.searchParams.append('client_id', KAKAO_REST_API_KEY);
    kakaoAuthUrl.searchParams.append('redirect_uri', REDIRECT_URI);
    kakaoAuthUrl.searchParams.append('response_type', 'code');
    kakaoAuthUrl.searchParams.append('state', state);

    console.log('Initiating Kakao login with state:', state);
    window.location.href = kakaoAuthUrl.toString();
  } catch (error) {
    logError('initiateKakaoLogin', error);
    throw error;
  }
};

// 카카오 토큰 발급
export const getKakaoToken = async (code: string) => {
  try {
    const KAKAO_REST_API_KEY = 'feeb3a5546b4caa1e4e3364aee4b583e';
    const REDIRECT_URI = `${window.location.origin}/auth/kakao/callback`;

    const params = new URLSearchParams();
    params.append('grant_type', 'authorization_code');
    params.append('client_id', KAKAO_REST_API_KEY);
    params.append('redirect_uri', REDIRECT_URI);
    params.append('code', code);

    const response = await fetch('https://kauth.kakao.com/oauth/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Accept': 'application/json'
      },
      body: params.toString()
    });

    if (!response.ok) {
      throw new Error('Failed to get Kakao access token');
    }

    const data = await response.json();
    
    if (!data.access_token) {
      throw new Error('Access token not found in response');
    }

    // 토큰 저장
    localStorage.setItem('kakao_access_token', data.access_token);
    if (data.refresh_token) {
      localStorage.setItem('kakao_refresh_token', data.refresh_token);
    }
    if (data.id_token) {
      localStorage.setItem('kakao_id_token', data.id_token);
    }

    return data;
  } catch (error) {
    logError('getKakaoToken', error);
    throw error;
  }
};

// 카카오 사용자 정보 가져오기
export const getKakaoUserInfo = async (accessToken: string) => {
  try {
    const response = await fetch('https://kapi.kakao.com/v2/user/me', {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/x-www-form-urlencoded;charset=utf-8'
      }
    });

    if (!response.ok) {
      throw new Error('Failed to get Kakao user info');
    }

    return await response.json();
  } catch (error) {
    logError('getKakaoUserInfo', error);
    throw error;
  }
};

// 카카오 로그인 처리
export const processKakaoLogin = async (code: string) => {
  try {
    // 카카오 토큰 발급
    const tokens = await getKakaoToken(code);
    
    if (!tokens.access_token) {
      throw new Error('Failed to get Kakao access token');
    }

    // 카카오 사용자 정보 가져오기
    const userInfo = await getKakaoUserInfo(tokens.access_token);
    
    // Supabase에 사용자 생성/업데이트
    const { data, error } = await supabase.auth.signInWithPassword({
      email: userInfo.kakao_account?.email || `${userInfo.id}@kakao.com`,
      password: `kakao_${userInfo.id}` // 임시 비밀번호
    });

    if (error && error.message.includes('Invalid login credentials')) {
      // 사용자가 없으면 생성
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email: userInfo.kakao_account?.email || `${userInfo.id}@kakao.com`,
        password: `kakao_${userInfo.id}`,
        options: {
          data: {
            display_name: userInfo.kakao_account?.profile?.nickname || '카카오 사용자',
            photo_url: userInfo.kakao_account?.profile?.profile_image_url || '',
            provider: 'kakao',
            kakao_id: userInfo.id
          }
        }
      });

      if (signUpError) {
        throw signUpError;
      }

      return signUpData;
    } else if (error) {
      throw error;
    }

    return data;
  } catch (error) {
    logError('processKakaoLogin', error);
    throw error;
  }
};

// 로그아웃
export const logout = async () => {
  try {
    // 카카오 로그아웃
    const kakaoToken = localStorage.getItem('kakao_access_token');
    if (kakaoToken) {
      try {
        await fetch('https://kapi.kakao.com/v1/user/logout', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${kakaoToken}`,
            'Content-Type': 'application/x-www-form-urlencoded;charset=utf-8'
          }
        });
      } catch (error) {
        console.warn('Kakao logout failed:', error);
      }
    }

    // Supabase 로그아웃
    const { error } = await supabase.auth.signOut();
    if (error) {
      throw error;
    }

    // 로컬 스토리지 정리
    cleanupAllData();
  } catch (error) {
    logError('logout', error);
    // 에러가 발생해도 정리 수행
    cleanupAllData();
    throw error;
  }
};

// 모든 데이터 정리 함수
const cleanupAllData = () => {
  const keysToRemove = [
    'user_type',
    'auth_provider',
    'last_login',
    'kakao_access_token',
    'kakao_refresh_token',
    'kakao_id_token',
    'kakao_state',
    'google_access_token',
    'google_refresh_token',
    'google_id_token'
  ];
  
  keysToRemove.forEach(key => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
  
  sessionStorage.clear();
  console.log('All data cleanup completed');
};

// 현재 사용자 가져오기
export const getCurrentUser = async () => {
  const { data: { user }, error } = await supabase.auth.getUser();
  if (error) {
    throw error;
  }
  return user;
};

// 인증 상태 변경 리스너
export const onAuthStateChange = (callback: (user: any) => void) => {
  return supabase.auth.onAuthStateChange((event, session) => {
    callback(session?.user || null);
  });
};
